import { BusesForm, defaultBusCard } from "./BusesForm";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useCustomContext } from "@/_helper/customizer";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { busCardKeys, useCreateBusCardMutation } from "@services/arrivals";
import { Spinner } from "react-bootstrap";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { FormProvider, useForm } from "react-hook-form";
import { PickKeyPairs } from "@/utils/helpers";
import { useEffect, useRef } from "react";

export const ReceptionCardModal = ({
  isOpen,
  toggle,
  arrival,
  date,
  params,
  selectedArrivalIndex,
}) => {
  const form = useForm();
  const hasServerError = useRef(false);

  const { layout } = useCustomContext();
  const dir = layout === "rtl" ? "ltr" : "rtl";
  const { t } = useTranslation();
  const { cachedBasicData } = useCachedBasicData();
  const serverDefaultValues =
    arrival?.bus_cards?.map((bus) => PickKeyPairs(bus, busCardKeys)) ?? [];

  // =============
  //  Side Effects
  // =============
  const { mutate: mutateBusCard, isPending: isBusCardPending } =
    useCreateBusCardMutation((field, error) => {
      hasServerError.current = true;
      form.setError(field, error);
    }, params);

  useEffect(() => {
    const { id: campaign_id, port } = arrival;

    const defaultValues = {
      direction_id: String(arrival?.direction_id) || "",
      port,
      campaign_id,
      arrival_type: arrival.type,
      arrival_id: arrival.id,
      bus_cards: [
        ...serverDefaultValues.map((bus) => ({
          ...bus,
          serverId: bus.id,
          serverValue: true,
        })),
        serverDefaultValues.length < 5 ? defaultBusCard : undefined,
      ].filter(Boolean) || [defaultBusCard],
    }

    form.reset(defaultValues);
  }, [arrival]);

  const onSave = (payload) => {
    const totalAddedCaapacity = payload?.bus_cards?.reduce((acc, bus) => +acc + (+bus?.capacity || 0), 0);
    const isTotalAddedCapacityValid = totalAddedCaapacity <= arrival?.hjaj_number;

    const { serverValue, serverId, ...data } = payload;

    const lastBusItem = data?.bus_cards?.[data?.bus_cards?.length - 1];
    if (!lastBusItem?.bus_id) {
      data.bus_cards.splice(data.bus_cards.length - 1, 1);
    }

    // Send the full Bus from basic data to the server
    data.bus_cards = data.bus_cards.map(({ serverId, serverValue, departure_date, ...bus }) => {
      const { q, ...cachedBus } =
        cachedBasicData.buses?.find(
          (b) => String(b.bus_id) === String(bus.bus_id)
        ) || {};

      if (cachedBus?.bus_id)
        cachedBus.bus_number = String(cachedBus.bus_number);

      return {
        ...(cachedBus || {}),
        ...bus,
      };
    });

    if (!isTotalAddedCapacityValid) {
      payload?.bus_cards.forEach((bus, index) => {
        const errorMessage = t("TOTAL_ADDED_CAPACITY_ERROR", {
          capacity: arrival?.hjaj_number,
        });

        form.setError(`bus_cards.${index}.capacity`, {
          type: "manual",
          message: errorMessage,
        });
      });

      return;
    }else {
      form.clearErrors("bus_cards");
    }

    mutateBusCard(
      { ...data, arrivalIndex: selectedArrivalIndex },
      {
        onSuccess: () => {
          toggle();
        }
      }
    );
  };
  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      keyboard={false}
    >
      <form onSubmit={form.handleSubmit(onSave)}>
        <FormProvider {...form}>
          <ModalHeader dir={dir} toggle={toggle}>
            <span className="fw-bold H5">{t("BUS_LIST_MODAL_TITLE")}</span>
          </ModalHeader>

          <ModalBody>
            <BusesForm
              params={params}
              {...{
                selectedArrivalIndex,
                arrival,
                date,
              }}
            />
          </ModalBody>

          <ModalFooter dir={dir}>
            <Button variant="danger" size="xs" type="button" onClick={toggle}>
              {t("CANCEL")}
            </Button>
            <button
              disabled={isBusCardPending}
              type="submit"
              className="btn btn-primary btn-xs"
            >
              {isBusCardPending ? <Spinner animation="border" size="sm" /> : null}
              {t("SAVE")}
            </button>
          </ModalFooter>
        </FormProvider>
      </form>
    </Modal>
  );
};